import React, { useEffect, useState } from "react";
import { Card, Empty, Space, Spin, Alert, Typography, Tag, Pagination, Collapse, Divider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { useApiCallback } from "../../hooks/useApiCallback";
import PageHeader from "../../components/common/PageHeader";
import type { CallbackItem } from "../../services/apiCallback";

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * Página de monitoramento de callbacks da API.
 *
 * Exibe todos os callbacks buscados para cada ID em formato de cards com paginação.
 */
const ApiCallbackMonitoringContent: React.FC = () => {
  const { callbackIds, results, getCallback, loading, error, setResults } = useApiCallback();
  const [processedIdsKey, setProcessedIdsKey] = useState<string>("");
  const [pageState, setPageState] = useState<Record<string, { current: number; pageSize: number }>>({});

  /**
   * Cria uma chave única baseada nos IDs para detectar quando os IDs mudam
   */
  const callbackIdsKey = callbackIds.join(",");

  /**
   * Efeito para buscar callbacks para cada ID quando os IDs mudam.
   */
  useEffect(() => {
    const fetchCallbacks = async () => {
      if (callbackIds.length === 0) {
        return;
      }

      // Se os IDs mudaram, limpa resultados anteriores e busca novamente
      if (callbackIdsKey !== processedIdsKey) {
        setResults({}); // Limpa resultados anteriores
        setProcessedIdsKey(callbackIdsKey); // Atualiza a chave processada

        // Busca callbacks para todos os IDs
        const promises = callbackIds.map(async (id) => {
          try {
            await getCallback(id);
          } catch (err) {
            console.error(`Erro ao buscar callback para ${id}:`, err);
          }
        });

        await Promise.all(promises);
      }
    };

    fetchCallbacks();
  }, [callbackIds, callbackIdsKey, processedIdsKey, getCallback, setResults]);

  // Inicializar estado de paginação para cada ID
  useEffect(() => {
    const newPageState: Record<string, { current: number; pageSize: number }> = {};
    callbackIds.forEach((id) => {
      if (!pageState[id]) {
        newPageState[id] = { current: 1, pageSize: 10 };
      } else {
        newPageState[id] = pageState[id];
      }
    });
    if (Object.keys(newPageState).length > 0) {
      setPageState(newPageState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackIds]);

  if (callbackIds.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Callbacks" />
          <Empty description="Nenhum ID para buscar callbacks. Volte para a página de API Callback para adicionar IDs." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  const isLoading = callbackIds.some((id) => loading[id]);

  if (isLoading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Callbacks" />
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "40px" }} />
        </Space>
      </Card>
    );
  }

  // Função para atualizar paginação de um ID específico
  const handlePageChange = (id: string, page: number, size: number) => {
    setPageState((prev) => ({
      ...prev,
      [id]: { current: page, pageSize: size },
    }));
  };

  // Calcular total de callbacks
  const totalCallbacks = callbackIds.reduce((sum, id) => {
    const result = results[id];
    if (result && result.success && result.data.callbacks) {
      return sum + result.data.callbacks.length;
    }
    return sum;
  }, 0);

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader title="Monitoramento de Callbacks" />
        {callbackIds.length > 0 && (
          <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
            Total de callbacks: {totalCallbacks} | IDs consultados: {callbackIds.length}
          </Text>
        )}
        {callbackIds.length === 0 && <Empty description="Nenhum ID para buscar callbacks. Volte para a página de API Callback para adicionar IDs." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        <Collapse>
          {callbackIds.map((id, idx) => {
            const result = results[id];
            const itemError = error[id];
            const itemLoading = loading[id];
            const callbacks = result && result.success ? result.data.callbacks : [];
            const externalId = result && result.success ? result.data.externalId : null;
            const pagination = pageState[id] || { current: 1, pageSize: 10 };

            // Calcular itens da página atual para este ID
            const startIndex = (pagination.current - 1) * pagination.pageSize;
            const endIndex = startIndex + pagination.pageSize;
            const currentCallbacks = callbacks.slice(startIndex, endIndex);

            return (
              <Panel
                key={idx}
                disabled={callbacks.length === 0}
                header={
                  <Space>
                    <Text strong>ID:</Text>
                    <Text copyable code>
                      {id}
                    </Text>
                    {externalId && (
                      <>
                        <Divider type="vertical" />
                        <Text strong>External ID:</Text>
                        <Text copyable code>
                          {externalId}
                        </Text>
                      </>
                    )}
                    {callbacks.length > 0 ? (
                      <Tag color="blue">
                        {callbacks.length} {callbacks.length === 1 ? "callback" : "callbacks"}
                      </Tag>
                    ) : (
                      <Tag color="error">Nenhum callback</Tag>
                    )}
                    {itemLoading && <Tag color="processing">Carregando...</Tag>}
                    {itemError && <Tag color="error">Erro</Tag>}
                  </Space>
                }
              >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {itemLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <Spin />
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">Buscando callbacks...</Text>
                      </div>
                    </div>
                  )}

                  {itemError && <Alert message="Erro ao buscar callback" description={itemError} type="error" showIcon style={{ marginBottom: 16 }} />}

                  {!itemLoading && !itemError && callbacks.length === 0 && <Empty description="Nenhum callback encontrado para este ID." image={Empty.PRESENTED_IMAGE_SIMPLE} />}

                  {!itemLoading && !itemError && callbacks.length > 0 && (
                    <>
                      <div>
                        {currentCallbacks.map((callback: CallbackItem, callbackIdx: number) => {
                          const isSuccess = callback.status === "success";
                          return (
                            <Card key={callbackIdx} size="small" style={{ marginBottom: 16 }}>
                              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                {/* Linha 1: Application (destacada), Status, Data de criação - sem títulos */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                                  <Tag color="purple" style={{ fontSize: "14px", padding: "4px 12px" }}>
                                    {callback.application}
                                  </Tag>
                                  <Tag color={isSuccess ? "success" : "error"} icon={isSuccess ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                                    {callback.status}
                                  </Tag>
                                  <Text type="secondary">{new Date(callback.createdAt).toLocaleString("pt-BR")}</Text>
                                </div>

                                {/* Linha 2: Method (destacado), URL, Retry, Tempo total */}
                                {callback.urlClient && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                                    <Tag color="blue">{callback.method}</Tag>
                                    <Text copyable ellipsis={{ tooltip: callback.urlClient }} style={{ wordBreak: "break-word" }}>
                                      {callback.urlClient}
                                    </Text>
                                    <Text type="secondary">|</Text>
                                    <Text type="secondary">retry: {callback.retry}</Text>
                                    <Text type="secondary">|</Text>
                                    <Text type="secondary">Tempo (s): {callback.timeToSendSeconds.toFixed(3)}</Text>
                                  </div>
                                )}

                                {/* Linha 3: Arquivo */}
                                {callback.file && (
                                  <div>
                                    <a href={callback.file} target="_blank" rel="noopener noreferrer">
                                      <LinkOutlined /> Abrir arquivo
                                    </a>
                                  </div>
                                )}
                              </Space>
                            </Card>
                          );
                        })}
                      </div>

                      {callbacks.length > pagination.pageSize && (
                        <div style={{ marginTop: 16, textAlign: "right" }}>
                          <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={callbacks.length}
                            showSizeChanger
                            showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} callbacks`}
                            pageSizeOptions={["10", "25", "50", "100"]}
                            onChange={(page, size) => {
                              handlePageChange(id, page, size);
                            }}
                            onShowSizeChange={(current, size) => {
                              handlePageChange(id, 1, size);
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Space>
              </Panel>
            );
          })}
        </Collapse>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página ApiCallbackMonitoring.
 */
const ApiCallbackMonitoring: React.FC = () => {
  return (
    <ApiCallbackMonitoringContent />
  );
};

export default ApiCallbackMonitoring;
